import { Typography } from "@/components/text";
import useProfile from "@/hooks/useProfile";
import { cn } from "@/libraries/cn";
import {
	PROTOCOL_TRIGGERS,
	type PlanItem,
	useCaseClinicalStore,
} from "@/stores/caseClinical";
import type { Case } from "@/types";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Switch from "@mui/material/Switch";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface CasePlanPanelProps {
	caseItem: Case;
}

const TRIGGER_KEYS: Record<string, string> = {
	"Spreading redness": "spreadingRedness",
	Fever: "fever",
	Malodour: "malodour",
	"Increasing pain": "increasingPain",
	"New numbness": "newNumbness",
	"Wound getting larger": "woundGettingLarger",
};

export const CasePlanPanel = memo(({ caseItem }: CasePlanPanelProps) => {
	const { t } = useTranslation("common");
	const { profile } = useProfile();
	const currentUserName = profile?.fullname || "Clinician";
	const caseId = String(caseItem?.id || "");

	const clinicalData = useCaseClinicalStore((s) => s.cases[caseId]);
	const updatePlanField = useCaseClinicalStore((s) => s.updatePlanField);
	const togglePlanTrigger = useCaseClinicalStore((s) => s.togglePlanTrigger);
	const togglePlanPatientFacing = useCaseClinicalStore(
		(s) => s.togglePlanPatientFacing,
	);
	const confirmPlanItem = useCaseClinicalStore((s) => s.confirmPlanItem);
	const discardPlanItem = useCaseClinicalStore((s) => s.discardPlanItem);
	const pushPlanToPatient = useCaseClinicalStore((s) => s.pushPlanToPatient);

	const planItems = clinicalData?.planItems ?? [];

	const [openCards, setOpenCards] = useState<Record<string, boolean>>({});
	const [editing, setEditing] = useState<{
		uid: string;
		field: "action" | "freq" | "review";
	} | null>(null);
	const [editValue, setEditValue] = useState("");

	const isOpen = (uid: string, defaultOpen: boolean) => {
		return openCards[uid] !== undefined ? openCards[uid] : defaultOpen;
	};

	const toggleCard = (uid: string, defaultOpen: boolean) => {
		setOpenCards((prev) => ({
			...prev,
			[uid]: !isOpen(uid, defaultOpen),
		}));
	};

	const handleShowSource = (srcId?: string) => {
		let el: HTMLElement | null = null;
		if (srcId) {
			el =
				document.getElementById(`msg-${srcId}`) ||
				document.querySelector(`[data-message-id="${srcId}"]`) ||
				document.querySelector(`[id*="${srcId}"]`);
		}
		if (!el) {
			el =
				document.querySelector('[data-tag="plan"]') ||
				document.querySelector('[data-tag="PLAN"]') ||
				document.querySelector('.message-box[data-tag*="plan"]');
		}
		if (!el) {
			const allBadges = Array.from(
				document.querySelectorAll(
					".message-box .badge, .message-box span, .message-box p",
				),
			);
			const planBadge = allBadges.find(
				(b) => b.textContent?.trim().toLowerCase() === "plan",
			);
			if (planBadge) {
				el =
					(planBadge.closest(".message-box") as HTMLElement) ||
					planBadge.parentElement;
			}
		}
		if (!el) {
			el = document.querySelector(".message-box") as HTMLElement;
		}

		const outerContainer = document.querySelector(
			".overflow-y-auto",
		) as HTMLElement | null;

		if (el) {
			if (outerContainer) {
				const containerRect = outerContainer.getBoundingClientRect();
				const elRect = el.getBoundingClientRect();
				const targetScrollTop =
					elRect.top - containerRect.top + outerContainer.scrollTop;
				outerContainer.scrollTo({
					top: targetScrollTop - 40,
					behavior: "smooth",
				});
			}
			el.scrollIntoView({ behavior: "smooth", block: "center" });
			const targetPanel =
				(el.querySelector(".MuiCard-root") as HTMLElement) ||
				(el.querySelector('[class*="MuiCard"]') as HTMLElement) ||
				el;
			targetPanel.classList.add(
				"ring-4",
				"ring-purple-500",
				"ring-offset-2",
				"transition-all",
				"duration-300",
			);
			setTimeout(() => {
				targetPanel.classList.remove(
					"ring-4",
					"ring-purple-500",
					"ring-offset-2",
				);
			}, 2500);
		} else if (outerContainer) {
			const mainEl = document.querySelector("main");
			if (mainEl) {
				const containerRect = outerContainer.getBoundingClientRect();
				const mainRect = mainEl.getBoundingClientRect();
				outerContainer.scrollTo({
					top: mainRect.top - containerRect.top + outerContainer.scrollTop,
					behavior: "smooth",
				});
			}
			toast.info(t("clinicalPlan.sourceInTimeline"));
		}
	};

	const handleStartEdit = (
		uid: string,
		field: "action" | "freq" | "review",
		currentVal: string,
	) => {
		setEditing({ uid, field });
		setEditValue(currentVal || "");
	};

	const handleSaveEdit = (uid: string, field: "action" | "freq" | "review") => {
		updatePlanField(caseId, uid, field, editValue.trim());
		setEditing(null);
	};

	const handleConfirmItem = (item: PlanItem) => {
		if (!item.action.trim()) {
			toast.error(t("clinicalPlan.actionRequired"));
			return;
		}
		const success = confirmPlanItem(caseId, item.uid, currentUserName);
		if (success) {
			toast.success(
				item.pf
					? t("clinicalPlan.confirmedPf")
					: t("clinicalPlan.confirmedInternal"),
			);
			setOpenCards((prev) => ({ ...prev, [item.uid]: item.pf }));
		}
	};

	const handleDiscard = (uid: string) => {
		discardPlanItem(caseId, uid);
		toast.info(t("clinicalPlan.discarded"));
	};

	const handleSendToPatient = (uid: string) => {
		pushPlanToPatient(caseId, uid);
		setOpenCards((prev) => ({ ...prev, [uid]: false }));
		toast.success(t("clinicalPlan.sentSuccess"));
	};

	return (
		<div className="w-full bg-[#f4f7fa] border-b border-zinc-200 py-3 shrink-0">
			<div className="container max-w-3xl w-full flex flex-col gap-3 px-4">
				{planItems.length === 0 ? (
					<div className="rounded-xl border border-zinc-200 bg-white py-8 px-6 text-center shadow-sm flex flex-col items-center justify-center">
						<div className="mb-3.5 flex h-14 w-14 items-center justify-center rounded-full bg-sky-50 text-sky-600">
							<PlaylistAddCheckRoundedIcon sx={{ fontSize: 28 }} />
						</div>
						<Typography
							fontWeight={700}
							fontSize={16}
							align="center"
							className="text-zinc-800 text-center"
						>
							{t("clinicalPlan.emptyTitle")}
						</Typography>
						<Typography
							fontSize={13}
							align="center"
							className="mt-1.5 text-zinc-500 max-w-md mx-auto text-center leading-relaxed"
						>
							{t("clinicalPlan.emptyDesc")}
						</Typography>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<div className="flex items-center justify-between">
							<span className="text-xs font-bold uppercase tracking-wider text-zinc-500">
								{t("clinicalPlan.summaryTitle")}{" "}
								<span className="text-xs font-normal normal-case text-zinc-400">
									{t("clinicalPlan.itemsCount", { count: planItems.length })}
								</span>
							</span>
						</div>

						{planItems.map((item) => {
							const isDraft = !item.confirmed;
							const open = isOpen(item.uid, isDraft);

							return (
								<div
									key={item.uid}
									className={cn(
										"rounded-xl p-4 shadow-sm transition-all",
										isDraft
											? "border-2 border-dashed border-amber-400 bg-[#fffdf9]"
											: "border border-emerald-200 bg-white",
									)}
								>
									{/* Card Header */}
									<button
										type="button"
										onClick={() => toggleCard(item.uid, isDraft)}
										className="w-full flex cursor-pointer items-center justify-between select-none text-left"
									>
										{isDraft ? (
											<span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
												<WarningAmberRoundedIcon fontSize="inherit" />
												{t("clinicalPlan.draftNotConfirmed")}
											</span>
										) : (
											<span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
												<CheckIcon fontSize="inherit" className="stroke-[3]" />
												{t("clinicalPlan.confirmed")}
											</span>
										)}
										<span className="text-zinc-400">
											{open ? (
												<KeyboardArrowUpIcon fontSize="small" />
											) : (
												<KeyboardArrowDownIcon fontSize="small" />
											)}
										</span>
									</button>

									{/* Folded view */}
									{!open ? (
										<div className="mt-2.5">
											<Typography
												fontSize={14}
												fontWeight={700}
												className="text-zinc-900"
											>
												{item.action || t("clinicalPlan.noActionStated")}
											</Typography>
											<div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-zinc-500">
												{[
													item.freq,
													item.who === "Patient"
														? t("clinicalPlan.whoPatient")
														: item.who === "Clinic"
															? t("clinicalPlan.whoClinic")
															: item.who,
													item.review
														? t("clinicalPlan.reviewPrefix", {
																date: item.review,
															})
														: "",
												]
													.filter(Boolean)
													.join(" · ")}
											</div>
											<div className="mt-2 flex flex-wrap items-center gap-1.5">
												<span
													className={cn(
														"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold",
														item.route === "ai"
															? "bg-purple-100 text-purple-700"
															: "bg-sky-100 text-sky-700",
													)}
												>
													{item.route === "ai" ? (
														<>
															<AutoAwesomeRoundedIcon
																style={{ fontSize: 11 }}
															/>
															{t("clinicalPlan.fromWoundCareAnalysis")}
														</>
													) : (
														<>
															<MessageOutlinedIcon style={{ fontSize: 11 }} />
															{t("clinicalPlan.fromTaggedMessage")}
														</>
													)}
												</span>
												{item.pushed ? (
													<span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700">
														<CheckIcon style={{ fontSize: 11 }} />
														{t("clinicalPlan.sentToPatient")}
													</span>
												) : item.confirmed && !item.pf ? (
													<span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-semibold text-zinc-600">
														{t("clinicalPlan.internal")}
													</span>
												) : null}
											</div>
										</div>
									) : (
										/* Expanded view */
										<div className="mt-3">
											{/* Route badge */}
											<div className="mb-2">
												<button
													type="button"
													onClick={() =>
														item.route === "msg" && handleShowSource(item.srcId)
													}
													className={cn(
														"inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
														item.route === "ai"
															? "bg-purple-100 text-purple-700 cursor-default"
															: "bg-sky-100 text-sky-700 hover:bg-sky-200",
													)}
												>
													{item.route === "ai" ? (
														<>
															<AutoAwesomeRoundedIcon fontSize="inherit" />
															{t("clinicalPlan.fromWoundCareAnalysis")}
														</>
													) : (
														<>
															<MessageOutlinedIcon fontSize="inherit" />
															{t("clinicalPlan.fromTaggedMessageWithDate", {
																when: item.srcWhen || "today",
															})}
														</>
													)}
												</button>
											</div>

											<div className="divide-y divide-zinc-100">
												{/* Action row */}
												<div className="py-2.5 text-sm">
													<span className="block text-xs font-medium text-zinc-500 mb-0.5">
														{t("clinicalPlan.fields.action")}{" "}
														<b className="text-red-500">*</b>
													</span>
													{editing?.uid === item.uid &&
													editing.field === "action" ? (
														<div className="mt-1 flex items-center gap-2">
															<input
																type="text"
																value={editValue}
																ref={(el) => el?.focus()}
																onChange={(e) => setEditValue(e.target.value)}
																onKeyDown={(e) => {
																	if (e.key === "Enter")
																		handleSaveEdit(item.uid, "action");
																	else if (e.key === "Escape") setEditing(null);
																}}
																className="flex-1 rounded-lg border border-sky-500 px-2.5 py-1 text-sm outline-none"
															/>
															<button
																type="button"
																onClick={() =>
																	handleSaveEdit(item.uid, "action")
																}
																className="rounded-lg bg-sky-600 px-3 py-1 text-xs font-bold text-white hover:bg-sky-700"
															>
																{t("save")}
															</button>
															<button
																type="button"
																onClick={() => setEditing(null)}
																className="rounded-lg border border-zinc-200 px-2 py-1 text-xs font-semibold text-zinc-600"
															>
																{t("cancel")}
															</button>
														</div>
													) : (
														<div className="flex items-center justify-between">
															<span
																className={cn(
																	"font-semibold break-words",
																	item.action
																		? "text-zinc-800"
																		: "text-zinc-400 italic font-normal",
																)}
															>
																{item.action || t("clinicalPlan.notStated")}
															</span>
															{isDraft ? (
																<button
																	type="button"
																	onClick={() =>
																		handleStartEdit(
																			item.uid,
																			"action",
																			item.action,
																		)
																	}
																	className="text-sky-600 hover:text-sky-700 p-1 flex-none"
																>
																	<EditOutlinedIcon fontSize="small" />
																</button>
															) : null}
														</div>
													)}
												</div>

												{/* Frequency row */}
												<div className="py-2.5 text-sm">
													<span className="block text-xs font-medium text-zinc-500 mb-0.5">
														{t("clinicalPlan.fields.freq")}
													</span>
													{editing?.uid === item.uid &&
													editing.field === "freq" ? (
														<div className="mt-1 flex items-center gap-2">
															<input
																type="text"
																value={editValue}
																ref={(el) => el?.focus()}
																onChange={(e) => setEditValue(e.target.value)}
																onKeyDown={(e) => {
																	if (e.key === "Enter")
																		handleSaveEdit(item.uid, "freq");
																	else if (e.key === "Escape") setEditing(null);
																}}
																placeholder={t(
																	"clinicalPlan.fields.freqPlaceholder",
																)}
																className="flex-1 rounded-lg border border-sky-500 px-2.5 py-1 text-sm outline-none"
															/>
															<button
																type="button"
																onClick={() => handleSaveEdit(item.uid, "freq")}
																className="rounded-lg bg-sky-600 px-3 py-1 text-xs font-bold text-white hover:bg-sky-700"
															>
																{t("save")}
															</button>
															<button
																type="button"
																onClick={() => setEditing(null)}
																className="rounded-lg border border-zinc-200 px-2 py-1 text-xs font-semibold text-zinc-600"
															>
																{t("cancel")}
															</button>
														</div>
													) : (
														<div className="flex items-center justify-between">
															<span
																className={cn(
																	"font-semibold",
																	item.freq
																		? "text-zinc-800"
																		: "text-zinc-400 italic font-normal",
																)}
															>
																{item.freq || t("clinicalPlan.notStated")}
															</span>
															{isDraft ? (
																<button
																	type="button"
																	onClick={() =>
																		handleStartEdit(item.uid, "freq", item.freq)
																	}
																	className="text-sky-600 hover:text-sky-700 p-1 flex-none"
																>
																	<EditOutlinedIcon fontSize="small" />
																</button>
															) : null}
														</div>
													)}
												</div>

												{/* Review date row */}
												<div className="py-2.5 text-sm">
													<span className="block text-xs font-medium text-zinc-500 mb-0.5">
														{t("clinicalPlan.fields.review")}
													</span>
													{editing?.uid === item.uid &&
													editing.field === "review" ? (
														<div className="mt-1 flex items-center gap-2">
															<input
																type="text"
																value={editValue}
																ref={(el) => el?.focus()}
																onChange={(e) => setEditValue(e.target.value)}
																onKeyDown={(e) => {
																	if (e.key === "Enter")
																		handleSaveEdit(item.uid, "review");
																	else if (e.key === "Escape") setEditing(null);
																}}
																placeholder={t(
																	"clinicalPlan.fields.reviewPlaceholder",
																)}
																className="flex-1 rounded-lg border border-sky-500 px-2.5 py-1 text-sm outline-none"
															/>
															<button
																type="button"
																onClick={() =>
																	handleSaveEdit(item.uid, "review")
																}
																className="rounded-lg bg-sky-600 px-3 py-1 text-xs font-bold text-white hover:bg-sky-700"
															>
																{t("save")}
															</button>
															<button
																type="button"
																onClick={() => setEditing(null)}
																className="rounded-lg border border-zinc-200 px-2 py-1 text-xs font-semibold text-zinc-600"
															>
																{t("cancel")}
															</button>
														</div>
													) : (
														<div className="flex items-center justify-between">
															<span
																className={cn(
																	"font-semibold",
																	item.review
																		? "text-zinc-800"
																		: "text-zinc-400 italic font-normal",
																)}
															>
																{item.review || t("clinicalPlan.notStated")}
															</span>
															{isDraft ? (
																<button
																	type="button"
																	onClick={() =>
																		handleStartEdit(
																			item.uid,
																			"review",
																			item.review,
																		)
																	}
																	className="text-sky-600 hover:text-sky-700 p-1 flex-none"
																>
																	<EditOutlinedIcon fontSize="small" />
																</button>
															) : null}
														</div>
													)}
												</div>

												{/* Who does it */}
												<div className="py-2.5 text-sm">
													<span className="block text-xs font-medium text-zinc-500 mb-1.5">
														{t("clinicalPlan.fields.who")}
													</span>
													{!isDraft ? (
														<span
															className={cn(
																"font-semibold",
																item.who
																	? "text-zinc-800"
																	: "text-zinc-400 italic font-normal",
															)}
														>
															{item.who === "Patient"
																? t("clinicalPlan.whoPatient")
																: item.who === "Clinic"
																	? t("clinicalPlan.whoClinic")
																	: item.who || t("clinicalPlan.notSet")}
														</span>
													) : (
														<div className="flex gap-2">
															<button
																type="button"
																onClick={() =>
																	updatePlanField(
																		caseId,
																		item.uid,
																		"who",
																		"Patient",
																	)
																}
																className={cn(
																	"rounded-lg border px-3.5 py-1.5 text-xs font-bold transition-all",
																	item.who === "Patient"
																		? "border-sky-600 bg-sky-50 text-sky-700"
																		: "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50",
																)}
															>
																{t("clinicalPlan.whoPatient")}
															</button>
															<button
																type="button"
																onClick={() =>
																	updatePlanField(
																		caseId,
																		item.uid,
																		"who",
																		"Clinic",
																	)
																}
																className={cn(
																	"rounded-lg border px-3.5 py-1.5 text-xs font-bold transition-all",
																	item.who === "Clinic"
																		? "border-sky-600 bg-sky-50 text-sky-700"
																		: "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50",
																)}
															>
																{t("clinicalPlan.whoClinic")}
															</button>
														</div>
													)}
												</div>

												{/* Escalation triggers */}
												<div className="py-2.5 text-sm">
													<span className="block text-xs font-medium text-zinc-500">
														{t("clinicalPlan.fields.triggers")}
													</span>
													<span className="block text-[11.5px] text-zinc-400 mb-2">
														{t("clinicalPlan.fields.triggersDesc")}
													</span>
													<div className="flex flex-wrap gap-1.5">
														{PROTOCOL_TRIGGERS.map((trigger) => {
															const on = item.trig.includes(trigger);
															if (!isDraft && !on) return null;

															return (
																<button
																	key={trigger}
																	type="button"
																	disabled={!isDraft}
																	onClick={() =>
																		togglePlanTrigger(caseId, item.uid, trigger)
																	}
																	className={cn(
																		"rounded-full border px-3 py-1 text-xs font-semibold transition-all",
																		on
																			? "border-red-300 bg-red-50 text-red-700 font-bold"
																			: "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50",
																		!isDraft && "cursor-default opacity-90",
																	)}
																>
																	{t(
																		`clinicalPlan.triggers.${TRIGGER_KEYS[trigger] ?? trigger}`,
																		trigger,
																	)}
																</button>
															);
														})}
														{!isDraft && item.trig.length === 0 ? (
															<span className="text-zinc-400 italic text-xs">
																{t("clinicalPlan.noneSet")}
															</span>
														) : null}
													</div>
												</div>

												{/* Patient-facing switch */}
												<div className="flex items-center justify-between py-2.5">
													<div>
														<span className="block text-sm font-semibold text-zinc-900">
															{t("clinicalPlan.patientFacing")}
														</span>
														<span className="block text-xs text-zinc-500">
															{item.pf
																? t("clinicalPlan.patientFacingDescTrue")
																: t("clinicalPlan.patientFacingDescFalse")}
														</span>
													</div>
													<Switch
														checked={item.pf}
														disabled={!isDraft}
														onChange={() =>
															togglePlanPatientFacing(caseId, item.uid)
														}
														color="primary"
													/>
												</div>
											</div>

											{/* Draft Action Buttons */}
											{isDraft ? (
												<div className="mt-4 flex items-center gap-2">
													<button
														type="button"
														onClick={() => handleDiscard(item.uid)}
														className="flex-1 rounded-lg border border-zinc-300 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50"
													>
														{t("clinicalPlan.discard")}
													</button>
													<button
														type="button"
														onClick={() => handleConfirmItem(item)}
														className="flex-1 rounded-lg bg-sky-600 py-2 text-xs font-bold text-white hover:bg-sky-700"
													>
														{t("clinicalPlan.confirmItem")}
													</button>
												</div>
											) : (
												<div className="mt-3 text-xs text-zinc-500 border-t pt-2.5">
													<p>
														{t("clinicalPlan.confirmedBy", {
															author: item.by,
															time: item.at,
														})}
													</p>
													{item.pf ? (
														item.pushed ? (
															<div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700">
																<CheckIcon fontSize="small" />
																{t("clinicalPlan.sentToAskHemi")}
															</div>
														) : (
															<button
																type="button"
																onClick={() => handleSendToPatient(item.uid)}
																className="mt-2.5 flex items-center justify-center gap-1.5 w-full rounded-lg bg-emerald-600 py-2 text-xs font-bold text-white hover:bg-emerald-700 shadow-sm"
															>
																<SendRoundedIcon fontSize="small" />
																{t("clinicalPlan.sendToPatient")}
															</button>
														)
													) : null}
												</div>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
});

CasePlanPanel.displayName = "CasePlanPanel";
export default CasePlanPanel;
